import { Link } from "react-router-dom";
import { SiteShell, useSite } from "@/components/site/SiteShell";

const SITE_URL = "www.brionigranite.com";

const Block = ({ n, title, children, testId }) => (
  <section
    className="grid gap-6 border-t border-[color:var(--s-line)] py-12 lg:grid-cols-[7rem_1fr]"
    data-testid={testId}
  >
    <div className="s-num text-2xl text-[color:var(--s-gold)]">
      {String(n).padStart(2, "0")}
    </div>
    <div>
      <h2 className="s-display text-2xl sm:text-3xl">{title}</h2>
      <div className="mt-5 space-y-4 text-sm leading-relaxed text-[color:var(--s-mute)]">
        {children}
      </div>
    </div>
  </section>
);

export default function SitePrivacy() {
  const site = useSite();
  const shop = site?.shop || {};
  const name = shop.name || "Brioni Granite";
  const phone = shop.phone || "";
  const email = shop.email || "";
  const strong = "text-[color:var(--s-ink)]";

  return (
    <SiteShell site={site} testId="site-privacy">
      <section className="px-6 pb-16 pt-6 sm:pt-8 lg:px-12">
        <div className="s-micro text-[color:var(--s-gold)]">Legal</div>
        <h1 className="s-display mt-8 max-w-3xl text-5xl sm:text-6xl">
          Privacy policy
        </h1>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-[color:var(--s-mute)]">
          This policy explains what {name} collects through {SITE_URL}, our
          quote forms, our customer portal and our text messages, what we do
          with it, and how to have it removed.
        </p>
      </section>

      <div className="px-6 pb-28 lg:px-12">
        <Block n={1} title="Who is responsible" testId="privacy-who">
          <p>
            {name}, a stone supplier and fabricator in St. Louis, Missouri, is
            responsible for the information described here. Contact us on{" "}
            {phone || "our shop number"}
            {email ? ` or at ${email}` : ""} with any privacy question.
          </p>
        </Block>

        <Block n={2} title="What we collect" testId="privacy-collect">
          <p>
            <strong className={strong}>You give us:</strong> your name, phone
            number, email, project address, the details and photos of your job,
            the colors you like, and anything you type into a form, the portal
            or a text message.
          </p>
          <p>
            <strong className={strong}>We create:</strong> quotes, drawings,
            invoices, slab holds, appointment records, and photos of your
            finished work.
          </p>
          <p>
            <strong className={strong}>Automatically:</strong> basic website
            analytics (pages visited, rough location, device type) if analytics
            is switched on, and the date, time and IP address recorded when you
            submit a form or tick a consent box.
          </p>
        </Block>

        <Block n={3} title="Why we use it" testId="privacy-why">
          <p>
            To answer your enquiry, quote your job, hold a slab, template,
            fabricate and install it, take payment, keep our books, and support
            you afterwards. We also use it to send you the updates you have
            asked for by email, text or through your portal.
          </p>
          <p>
            We do not sell your information, and we do not buy contact lists.
          </p>
        </Block>

        <Block
          n={4}
          title="Text messages and your mobile number"
          testId="privacy-sms"
        >
          <p>
            When you tick the texting box on a form, ask us to text you, or text
            us first, we record your number along with the date, the wording you
            agreed to and the IP address of the submission — that record is our
            proof of consent and is kept for as long as we are allowed to text
            you.
          </p>
          <p>
            <strong className={strong}>
              No mobile information is sold, rented or shared with third parties
              or affiliates for their own marketing or lead-generation purposes.
            </strong>{" "}
            Your number is shared only with the messaging provider that delivers
            our texts on our behalf, and only so the message can reach you.
          </p>
          <p>
            Reply <strong className={strong}>STOP</strong> to any text and we
            stop, straight away and permanently, until you ask us to start
            again. Reply <strong className={strong}>HELP</strong> for help. Full
            texting terms, message frequency and rates are in our{" "}
            <Link to="/terms" className="text-[color:var(--s-gold)] underline">
              terms &amp; conditions
            </Link>
            .
          </p>
        </Block>

        <Block n={5} title="Who else sees it" testId="privacy-sharing">
          <p>
            Only the people who need it to do the work: our own staff, and the
            services we run the business on — our messaging provider for texts,
            our email provider for emails, our card processor for payments, and
            our cloud storage for photos and documents. They act on our
            instructions and may not use your details for anything else.
          </p>
          <p>
            We may share what we have to if the law requires it, or to establish
            or defend a legal claim.
          </p>
        </Block>

        <Block n={6} title="Photos of your job" testId="privacy-photos">
          <p>
            We photograph finished work for our records. A photo only appears on{" "}
            {SITE_URL} or our social pages when the job has been ticked for the
            public gallery — tell us if you would rather your home was not shown
            and we will keep it private.
          </p>
        </Block>

        <Block n={7} title="How long we keep it" testId="privacy-retention">
          <p>
            Quotes, invoices and job records are kept for as long as tax and
            warranty rules require. Enquiries that never became jobs are cleared
            out periodically. Consent and opt-out records are kept as proof that
            we handled your preferences properly.
          </p>
        </Block>

        <Block n={8} title="Your choices" testId="privacy-choices">
          <p>
            You can ask us what we hold about you, have it corrected, ask for it
            to be deleted where we are not required to keep it, stop texts by
            replying STOP, or stop emails by telling us. Call{" "}
            {phone || "the shop"}
            {email ? ` or email ${email}` : ""} and we will sort it.
          </p>
        </Block>

        <Block n={9} title="Security" testId="privacy-security">
          <p>
            Access to our system is restricted by role and protected by
            passwords; card details are handled entirely by our payment
            processor and never stored by us. No system is perfect, so please do
            not send sensitive information such as card numbers by text or
            email.
          </p>
        </Block>

        <Block n={10} title="Children" testId="privacy-children">
          <p>
            Our services are for adults arranging work on a property. We do not
            knowingly collect information from children.
          </p>
        </Block>

        <Block n={11} title="Changes" testId="privacy-changes">
          <p>
            We update this policy when our services change; the current version
            always lives at {SITE_URL}/privacy.
          </p>
        </Block>

        <div className="border-t border-[color:var(--s-line)] pt-12">
          <p className="text-sm text-[color:var(--s-mute)]">
            Prefer to talk it through? Call {phone || "the shop"} or{" "}
            <Link
              to="/contact"
              className="text-[color:var(--s-gold)] underline"
            >
              send us a message
            </Link>
            .
          </p>
        </div>
      </div>
    </SiteShell>
  );
}
